import { Button, TextField, Tooltip } from '@material-ui/core'
import BlockIcon from '@material-ui/icons/Block'
import EditIcon from '@material-ui/icons/Edit'
import LocalShippingIcon from '@material-ui/icons/LocalShipping'
import DeleteIcon from '@material-ui/icons/Delete'
import React, { useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import {
	Card,
	CardBody,
	CardHeader,
	CardHeaderToolbar,
} from '../../../../_metronic/_partials/controls'
import { deleteContract, getContracts, noticeContract } from '../../../../api/contract'
import { entregarGpsDevices } from '../../../../api/gpsdevice'
import { alertError, alertSuccess } from '../../../../utils/logger'
import ConfirmDialog from '../../../components/dialogs/ConfirmDialog'
import Table, { buttonsStyle } from '../../../components/tables/table'

const euros = (centimos) =>
	new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format((centimos ?? 0) / 100)

const fecha = (d) => (d ? new Date(d).toLocaleDateString('es-ES') : '—')

/** Un estado se lee mejor de un vistazo que leyendo una palabra en gris. */
const COLORES = {
	activo: '#1b8a5a',
	pendiente: '#8a6d1b',
	impagado: '#c0392b',
	terminado: '#7b8794',
}

function getData(contratos) {
	return contratos.map((c) => ({
		id: c._id,
		club: c.club?.fullName ?? '— sin club',
		tipo: c,
		aparatos: c,
		precio: c,
		fechas: c,
		estado: c.estado,
		data: c,
	}))
}

export default function ContractsPage() {
	const [data, setData] = useState([])
	const [contractId, setContractId] = useState(null)
	const [openConfirmDialog, setOpenConfirmDialog] = useState(null)
	const [seleccionado, setSeleccionado] = useState(null)
	const [avisoEn, setAvisoEn] = useState(new Date().toISOString().slice(0, 10))
	const [refresh, setRefresh] = useState(false)
	const history = useHistory()

	function tipoFormatter(c) {
		if (c.pilot)
			return <span style={{ color: COLORES.pendiente }}>piloto</span>
		if (c.kind === 'regatta')
			return 'pack regata'
		return c.season ? 'temporada' : 'suscripción'
	}

	/**
	 * Entregados contra contratados. Que se vea "18/25" y no dos columnas
	 * sueltas: lo que importa es si falta mandar aparatos, no cada número.
	 */
	function aparatosFormatter(c) {
		const faltan = (c.devices ?? 0) - (c.devicesDelivered ?? 0)
		return (
			<span style={{ whiteSpace: 'nowrap' }}>
				{c.devicesDelivered ?? 0}/{c.devices ?? 0}
				{faltan > 0 && (
					<span style={{ color: COLORES.impagado, marginLeft: 6 }} title="Pendientes de entregar">
						faltan {faltan}
					</span>
				)}
			</span>
		)
	}

	function precioFormatter(c) {
		if (c.pilot)
			return '—'
		if (c.kind === 'regatta')
			return euros(c.totalCents)
		return `${euros(c.monthlyCents)}/mes`
	}

	function fechasFormatter(c) {
		return (
			<span style={{ whiteSpace: 'nowrap' }}>
				{fecha(c.startDate)}
				{c.endDate ? ` → ${fecha(c.endDate)}` : ''}
				{!c.endDate && c.commitmentEndDate && (
					<span style={{ color: '#7b8794' }}> · compromiso hasta {fecha(c.commitmentEndDate)}</span>
				)}
			</span>
		)
	}

	function estadoFormatter(estado) {
		return <span style={{ color: COLORES[estado] ?? '#7b8794', fontWeight: 600 }}>{estado}</span>
	}

	function buttonFormatter(c) {
		return (
			<>
				<Tooltip title="Editar">
					<Button
						style={buttonsStyle}
						size="small"
						onClick={() => history.push('/edit-contract/' + c._id)}
					>
						<EditIcon />
					</Button>
				</Tooltip>
				{(c.devices ?? 0) - (c.devicesDelivered ?? 0) > 0 && (
					<Tooltip title="Entregarle los aparatos que faltan">
						<Button
							style={buttonsStyle}
							size="small"
							onClick={() => {
								setContractId(c._id)
								setSeleccionado(c)
								setOpenConfirmDialog(3)
							}}
						>
							<LocalShippingIcon />
						</Button>
					</Tooltip>
				)}
				{!c.endDate && (
					<Tooltip title="Anotar aviso de baja">
						<Button
							style={buttonsStyle}
							size="small"
							onClick={() => {
								setContractId(c._id)
								setOpenConfirmDialog(1)
							}}
						>
							<BlockIcon />
						</Button>
					</Tooltip>
				)}
				<Tooltip title="Borrar">
					<Button
						style={buttonsStyle}
						size="small"
						onClick={() => {
							setContractId(c._id)
							setOpenConfirmDialog(2)
						}}
					>
						<DeleteIcon />
					</Button>
				</Tooltip>
			</>
		)
	}

	const columns = [
		{ dataField: 'club', text: 'Club', sort: true },
		{ dataField: 'tipo', text: 'Tipo', formatter: tipoFormatter },
		{ dataField: 'aparatos', text: 'Aparatos', formatter: aparatosFormatter },
		{ dataField: 'precio', text: 'Precio', formatter: precioFormatter },
		{ dataField: 'fechas', text: 'Vigencia', formatter: fechasFormatter },
		{ dataField: 'estado', text: 'Estado', formatter: estadoFormatter, sort: true },
		{
			dataField: 'data',
			text: '',
			formatter: buttonFormatter,
			align: 'end',
			style: { whiteSpace: 'nowrap' },
		},
	]

	useEffect(() => {
		getContracts()
			.then((res) => {
				if (res.status === 200) {
					setData(getData(res.data))
					setRefresh(false)
				}
			})
			.catch((error) => {
				alertError({ error, customMessage: 'No se pudieron cargar los contratos.' })
			})
	}, [refresh])

	return (
		<Card>
			<CardHeader title="Contratos">
				<CardHeaderToolbar>
					<button
						type="button"
						className="btn btn-light mr-2"
						onClick={() => history.push('/tarifas')}
					>
						Envío y asistencia
					</button>
					<button type="button" className="btn btn-primary" onClick={() => history.push('/alta-club')}>
						Dar de alta un club
					</button>
				</CardHeaderToolbar>
			</CardHeader>
			<CardBody>
				<Table data={data} columns={columns} />

				<ConfirmDialog
					title="Anotar el aviso de baja"
					open={openConfirmDialog === 1}
					setOpen={setOpenConfirmDialog}
					onConfirm={() => {
						noticeContract(contractId, avisoEn)
							.then((res) => {
								if (res.status === 200) {
									alertSuccess({
										title: 'Anotado',
										customMessage: `Deja de tener servicio el ${fecha(res.data.endDate)}.`,
									})
									setRefresh(true)
								}
							})
							.catch((error) => {
								alertError({ error, customMessage: 'No se pudo anotar la baja.' })
							})
					}}
				>
					{/* La fecha del aviso manda: de ella salen los 30 días. */}
					<TextField
						label="¿Cuándo avisó el club?"
						type="date"
						value={avisoEn}
						onChange={(e) => setAvisoEn(e.target.value)}
						InputLabelProps={{ shrink: true }}
						fullWidth
					/>
					<p style={{ marginTop: 12, color: '#7b8794' }}>
						El contrato sigue activo hasta la fecha que salga: no antes de cumplir el compromiso, no
						antes de 30 días desde el aviso, y siempre en un día de renovación.
					</p>
				</ConfirmDialog>

				<ConfirmDialog
					title="Entregarle los aparatos que faltan"
					open={openConfirmDialog === 3}
					setOpen={setOpenConfirmDialog}
					onConfirm={() => {
						const faltan = (seleccionado?.devices ?? 0) - (seleccionado?.devicesDelivered ?? 0)
						entregarGpsDevices(seleccionado?.club?._id ?? seleccionado?.club, faltan)
							.then((res) => {
								// Puede no haber bastantes en existencias: se dice, no se falla.
								alertSuccess({
									title: 'Entregados',
									customMessage: res.data.pendientes
										? `${res.data.entregados} entregados. Faltan ${res.data.pendientes} por pedir al proveedor.`
										: `${res.data.entregados} aparatos entregados.`,
								})
								setRefresh(true)
							})
							.catch((error) => {
								alertError({ error, customMessage: 'No se pudieron entregar los aparatos.' })
							})
					}}
				>
					<p>
						Se le asignan los aparatos que le faltan, cogiendo de las existencias los mas antiguos
						primero. Si no hay bastantes, se entregan los que haya y te decimos cuantos quedan por
						pedir.
					</p>
				</ConfirmDialog>

				<ConfirmDialog
					title="¿Seguro que quieres borrar este contrato?"
					open={openConfirmDialog === 2}
					setOpen={setOpenConfirmDialog}
					onConfirm={() => {
						deleteContract(contractId)
							.then((res) => {
								if (res.status === 204) {
									alertSuccess({ title: 'Borrado', customMessage: 'Contrato eliminado.' })
									setRefresh(true)
								}
							})
							.catch((error) => {
								alertError({ error, customMessage: 'No se pudo borrar el contrato.' })
							})
					}}
				/>
			</CardBody>
		</Card>
	)
}
